import React, { useState, useEffect, useRef } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import client from '../../api/client'
import { ArrowRight, ExternalLink, MessageCircle } from 'lucide-react'

export default function AdCarousel({ ads = [], variant = 'large' }) {
  const [currentIndex, setCurrentIndex] = useState(0)
  const [isHovered, setIsHovered] = useState(false)
  const timerRef = useRef(null)

  const activeAds = ads.filter(ad => ad.estado === 'activo')

  const baseImgUrl = (import.meta.env.VITE_API_URL && import.meta.env.VITE_API_URL.startsWith('http'))
    ? import.meta.env.VITE_API_URL.replace('/api', '')
    : (window.__APP_BASE_URL__ || '/').replace(/\/$/, '')

  const currentAd = activeAds[currentIndex]
  const isVideo = currentAd?.imagen_ruta?.toLowerCase().endsWith('.mp4') || currentAd?.imagen_ruta?.toLowerCase().endsWith('.webm');

  const getWhatsAppLink = (number) => {
    if (!number) return ''
    const cleanNumber = number.replace(/\D/g, '')
    if (cleanNumber.length === 8) {
      return `https://wa.me/591${cleanNumber}`
    }
    return `https://wa.me/${cleanNumber}`
  }

  const primaryLink = currentAd ? (currentAd.enlace_url || (currentAd.whatsapp ? getWhatsAppLink(currentAd.whatsapp) : '#')) : '#'

  const handleVideoEnded = () => {
    if (activeAds.length > 1 && !isHovered) {
      setCurrentIndex((prevIndex) => (prevIndex + 1) % activeAds.length)
    }
  }

  useEffect(() => {
    if (activeAds.length <= 1) return

    if (!isHovered) {
      let delay = 5000 // Por defecto 5 segundos para imágenes
      
      if (isVideo) {
        const customDuration = parseInt(currentAd?.duracion_video)
        if (customDuration > 0) {
          delay = customDuration * 1000
        } else {
          // Si es un video sin duración configurada, no ponemos timeout.
          // La transición se manejará mediante el evento onEnded.
          return
        }
      }

      timerRef.current = setTimeout(() => {
        setCurrentIndex((prevIndex) => (prevIndex + 1) % activeAds.length)
      }, delay)
    }

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current)
      }
    }
  }, [activeAds.length, currentIndex, isHovered, isVideo, currentAd])

  if (activeAds.length === 0) return null

  const handleAdClick = async (ad) => {
    try {
      // Registrar clic de forma asíncrona en segundo plano
      client.post(`/ads/${ad.id}/click`).catch(err => {
        console.error('Error tracking ad click:', err)
      })
    } catch (e) {
      // Ignorar errores del tracker
    }
  }

  // Declaraciones movidas al inicio del componente para uso en el hook useEffect


  if (variant === 'compact') {
    // Ribbon Footer Compacto con alto contraste, borde superior de neón cyan y sombra.
    return (
      <div 
        className="w-full bg-gradient-to-r from-navy-dark/95 via-navy/90 to-navy-dark/95 border-t border-cyan/40 py-3 md:py-2 px-4 overflow-hidden relative shadow-[0_-8px_25px_rgba(0,194,255,0.08)]"
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <div className="max-w-7xl mx-auto">
          {/* VISTA MÓVIL (Acordeón Vertical) - visible solo en móviles */}
          <div className="flex flex-col items-center gap-3 md:hidden">
            <span className="bg-cyan/15 text-cyan border border-cyan/45 px-2.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider glow-cyan">
              Auspiciador
            </span>
            
            <AnimatePresence mode="wait">
              <motion.div
                key={currentAd.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.3 }}
                className="w-full max-w-xs flex flex-col items-center"
              >
                <a
                  href={primaryLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => handleAdClick(currentAd)}
                  className="w-full aspect-[4/1] rounded-xl overflow-hidden bg-gradient-to-br from-navy-dark/95 via-navy/85 to-navy-dark/95 border border-cyan/30 shadow-[0_0_15px_rgba(0,194,255,0.08)] hover:border-cyan/60 transition-all duration-300 relative block"
                >
                  {isVideo ? (
                    <video
                      key={currentAd.id}
                      src={`${baseImgUrl}${currentAd.imagen_ruta}`}
                      className="w-full h-full object-contain block"
                      autoPlay
                      muted
                      loop={parseInt(currentAd?.duracion_video) > 0}
                      playsInline
                      onEnded={handleVideoEnded}
                    />
                  ) : (
                    <img
                      src={`${baseImgUrl}${currentAd.imagen_ruta}`}
                      alt={currentAd.titulo}
                      className="w-full h-full object-contain block"
                      onError={(e) => {
                        e.target.style.display = 'none'
                      }}
                    />
                  )}
                </a>
                
                <div className="mt-2.5 flex items-center justify-center gap-2">
                  <span className="font-semibold text-text-primary text-xs truncate max-w-[120px]">
                    {currentAd.titulo}
                  </span>
                  <div className="flex items-center gap-1.5">
                    {currentAd.enlace_url && (
                      <a
                        href={currentAd.enlace_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={() => handleAdClick(currentAd)}
                        className="p-1 rounded bg-cyan/15 text-cyan hover:bg-cyan/25 transition-colors flex items-center justify-center"
                        title="Visitar Sitio"
                      >
                        <ExternalLink className="w-3.5 h-3.5" />
                      </a>
                    )}
                    {currentAd.whatsapp && (
                      <a
                        href={getWhatsAppLink(currentAd.whatsapp)}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={() => handleAdClick(currentAd)}
                        className="p-1 rounded bg-[#25D366]/15 text-[#25D366] hover:bg-[#25D366]/25 transition-colors flex items-center justify-center"
                        title="Contactar WhatsApp"
                      >
                        <MessageCircle className="w-3.5 h-3.5" />
                      </a>
                    )}
                  </div>
                </div>
              </motion.div>
            </AnimatePresence>

            {/* Dots móviles */}
            {activeAds.length > 1 && (
              <div className="flex gap-1.5 justify-center mt-1">
                {activeAds.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentIndex(index)}
                    className={`w-1.5 h-1.5 rounded-full transition-all ${
                      index === currentIndex 
                        ? 'bg-cyan w-3 shadow-[0_0_8px_rgba(0,194,255,0.8)]' 
                        : 'bg-text-muted/40 hover:bg-cyan/40'
                    }`}
                    aria-label={`Ir al auspiciador ${index + 1}`}
                  />
                ))}
              </div>
            )}
          </div>

          {/* VISTA ESCRITORIO (Ribbon Horizontal Delgado) - visible desde md */}
          <div className="hidden md:flex flex-row items-center justify-between gap-6 text-xs h-16">
            {/* Lado izquierdo: Badge */}
            <div className="flex items-center gap-2 shrink-0">
              <span className="bg-cyan/15 text-cyan border border-cyan/45 px-2.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider glow-cyan">
                Auspiciador
              </span>
            </div>

            {/* Centro: Banner (176x44) y Título con link */}
            <div className="flex-1 flex items-center justify-center overflow-hidden h-full">
              <AnimatePresence mode="wait">
                <motion.div
                  key={currentAd.id}
                  initial={{ opacity: 0, x: 15 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -15 }}
                  transition={{ duration: 0.3 }}
                  className="flex items-center gap-5 h-full"
                >
                  {/* Banner horizontal legible */}
                  <a
                    href={primaryLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => handleAdClick(currentAd)}
                    className="h-11 w-44 rounded-lg overflow-hidden bg-navy-dark/95 border border-cyan/25 hover:border-cyan/50 hover:shadow-[0_0_15px_rgba(0,194,255,0.12)] transition-all duration-300 block"
                  >
                    {isVideo ? (
                      <video
                        key={currentAd.id}
                        src={`${baseImgUrl}${currentAd.imagen_ruta}`}
                        className="w-full h-full object-contain"
                        autoPlay
                        muted
                        loop={parseInt(currentAd?.duracion_video) > 0}
                        playsInline
                        onEnded={handleVideoEnded}
                      />
                    ) : (
                      <img
                        src={`${baseImgUrl}${currentAd.imagen_ruta}`}
                        alt={currentAd.titulo}
                        className="w-full h-full object-contain"
                        onError={(e) => {
                          e.target.style.display = 'none'
                        }}
                      />
                    )}
                  </a>

                  {/* Texto y Enlaces */}
                  <div className="flex items-center gap-4">
                    <span className="font-semibold text-text-primary text-sm truncate max-w-[150px]">
                      {currentAd.titulo}
                    </span>
                    <div className="flex items-center gap-2.5">
                      {currentAd.enlace_url && (
                        <a
                          href={currentAd.enlace_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={() => handleAdClick(currentAd)}
                          className="flex items-center gap-1 px-2.5 py-1 bg-cyan/15 text-cyan hover:bg-cyan-light hover:scale-105 active:scale-95 border border-cyan/35 rounded-lg transition-all text-xs font-semibold cursor-pointer"
                        >
                          Sitio <ExternalLink className="w-3.5 h-3.5" />
                        </a>
                      )}
                      {currentAd.whatsapp && (
                        <a
                          href={getWhatsAppLink(currentAd.whatsapp)}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={() => handleAdClick(currentAd)}
                          className="flex items-center gap-1 px-2.5 py-1 bg-[#25D366]/15 text-[#25D366] hover:bg-[#20ba5a] hover:text-white hover:scale-105 active:scale-95 border border-[#25D366]/35 rounded-lg transition-all text-xs font-semibold cursor-pointer"
                        >
                          WhatsApp <MessageCircle className="w-3.5 h-3.5" />
                        </a>
                      )}
                    </div>
                  </div>
                </motion.div>
              </AnimatePresence>
            </div>

            {/* Lado derecho: Dots escritorio */}
            {activeAds.length > 1 && (
              <div className="flex gap-1.5 shrink-0 justify-center">
                {activeAds.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentIndex(index)}
                    className={`w-2 h-2 rounded-full transition-all ${
                      index === currentIndex 
                        ? 'bg-cyan w-4 shadow-[0_0_8px_rgba(0,194,255,0.8)]' 
                        : 'bg-text-muted/40 hover:bg-cyan/40'
                    }`}
                    aria-label={`Ir al auspiciador ${index + 1}`}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    )
  }

  if (variant === 'banner-only') {
    return (
      <div 
        className="w-full relative overflow-hidden"
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <div className="max-w-4xl mx-auto px-4">
          <div className="relative w-full pb-6">
            <AnimatePresence mode="wait">
              <motion.div
                key={currentAd.id}
                initial={{ opacity: 0, scale: 0.98 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.98 }}
                transition={{ duration: 0.5, ease: 'easeInOut' }}
                className="w-full"
              >
                {/* Vista móvil */}
                <div className="flex flex-col md:hidden w-full items-center animate-fade-in">
                  <a
                    href={currentAd.enlace_url || '#'}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => handleAdClick(currentAd)}
                    className="w-full rounded-2xl overflow-hidden bg-gradient-to-br from-navy-dark/95 via-navy/85 to-navy-dark/95 border-2 border-cyan/40 shadow-[0_0_20px_rgba(0,194,255,0.12)] hover:border-cyan/60 transition-all duration-300 relative block"
                  >
                    {isVideo ? (
                      <video
                        key={currentAd.id}
                        src={`${baseImgUrl}${currentAd.imagen_ruta}`}
                        className="w-full h-auto object-contain block"
                        autoPlay
                        muted
                        loop={parseInt(currentAd?.duracion_video) > 0}
                        playsInline
                        onEnded={handleVideoEnded}
                      />
                    ) : (
                      <img
                        src={`${baseImgUrl}${currentAd.imagen_ruta}`}
                        alt={currentAd.titulo}
                        className="w-full h-auto object-contain block"
                        onError={(e) => {
                          e.target.style.display = 'none'
                        }}
                      />
                    )}
                  </a>
                </div>

                {/* Vista escritorio */}
                <div className="hidden md:flex flex-col w-full items-center animate-fade-in">
                  <a
                    href={primaryLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => handleAdClick(currentAd)}
                    className="w-full aspect-[4/1] rounded-2xl overflow-hidden bg-gradient-to-br from-navy-dark/95 via-navy/85 to-navy-dark/95 border-2 border-cyan/40 shadow-[0_0_30px_rgba(0,194,255,0.18)] hover:border-cyan/60 hover:shadow-[0_0_35px_rgba(0,194,255,0.25)] transition-all duration-300 relative block"
                  >
                    {isVideo ? (
                      <video
                        key={currentAd.id}
                        src={`${baseImgUrl}${currentAd.imagen_ruta}`}
                        className="w-full h-full object-contain block"
                        autoPlay
                        muted
                        loop={parseInt(currentAd?.duracion_video) > 0}
                        playsInline
                        onEnded={handleVideoEnded}
                      />
                    ) : (
                      <img
                        src={`${baseImgUrl}${currentAd.imagen_ruta}`}
                        alt={currentAd.titulo}
                        className="w-full h-full object-contain block"
                        onError={(e) => {
                          e.target.style.display = 'none'
                        }}
                      />
                    )}
                  </a>
                </div>
              </motion.div>
            </AnimatePresence>

            {/* Controles de navegación */}
            {activeAds.length > 1 && (
              <div className="absolute bottom-0 left-1/2 -translate-x-1/2 flex gap-2 z-20">
                {activeAds.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentIndex(index)}
                    className={`w-2 h-2 rounded-full transition-all ${
                      index === currentIndex 
                        ? 'bg-cyan w-5 shadow-[0_0_8px_rgba(0,194,255,0.8)]' 
                        : 'bg-text-muted/30 hover:bg-cyan/45'
                    }`}
                    aria-label={`Ir a banner ${index + 1}`}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    )
  }


  // Carrusel Vistoso / Premium para la Landing Page
  return (
    <div 
      className="w-full py-8 relative overflow-hidden"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Luces decorativas traseras */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[180px] bg-cyan/10 rounded-full blur-[110px] pointer-events-none" />
      
      <div className="max-w-4xl mx-auto px-4">
        <div className="relative w-full pb-8">
          <AnimatePresence mode="wait">
            <motion.div
              key={currentAd.id}
              initial={{ opacity: 0, scale: 0.97 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.97 }}
              transition={{ duration: 0.5, ease: 'easeInOut' }}
              className="w-full"
            >
              {/* Layout para móviles (Flex Col) */}
              <div className="flex flex-col md:hidden w-full items-center">
                {/* Etiqueta arriba del banner */}
                <span className="mb-3 text-[9px] font-bold tracking-widest text-cyan uppercase bg-cyan/15 px-2.5 py-1 rounded-full border border-cyan/35 shadow-[0_0_10px_rgba(0,194,255,0.05)]">
                  Auspiciador Oficial Mundial 2026
                </span>

                {/* El Banner (La Tarjeta con borde cyan que contiene la imagen ajustada para evitar recortes) */}
                <div className="w-full rounded-2xl overflow-hidden bg-gradient-to-br from-navy-dark/95 via-navy/85 to-navy-dark/95 border-2 border-cyan/40 shadow-[0_0_25px_rgba(0,194,255,0.15)] relative">
                  {isVideo ? (
                    <video
                      key={currentAd.id}
                      src={`${baseImgUrl}${currentAd.imagen_ruta}`}
                      className="w-full h-auto object-contain block"
                      autoPlay
                      muted
                      loop={parseInt(currentAd?.duracion_video) > 0}
                      playsInline
                      onEnded={handleVideoEnded}
                    />
                  ) : (
                    <img
                      src={`${baseImgUrl}${currentAd.imagen_ruta}`}
                      alt={currentAd.titulo}
                      className="w-full h-auto object-contain block"
                      onError={(e) => {
                        e.target.style.display = 'none'
                      }}
                    />
                  )}
                </div>
                     {/* Contenido FUERA del banner (debajo) */}
                  <div className="mt-4 flex flex-col items-center text-center space-y-3 px-2 pb-4">
                    <h3 className="font-display font-bold text-lg text-text-primary">
                      {currentAd.titulo}
                    </h3>
                    
                    <div className="flex flex-wrap gap-2.5 items-center justify-center">
                      {currentAd.enlace_url && (
                        <a
                          href={currentAd.enlace_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={() => handleAdClick(currentAd)}
                          className="inline-flex items-center gap-2 px-5 py-2.5 bg-cyan text-navy-dark hover:bg-cyan-light hover:scale-105 active:scale-95 transition-all text-xs font-semibold rounded-xl font-display shadow-lg shadow-cyan/20 cursor-pointer"
                        >
                          Visitar Sitio
                          <ExternalLink className="w-3.5 h-3.5" />
                        </a>
                      )}
                      {currentAd.whatsapp && (
                        <a
                          href={getWhatsAppLink(currentAd.whatsapp)}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={() => handleAdClick(currentAd)}
                          className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#25D366] text-white hover:bg-[#20ba5a] hover:scale-105 active:scale-95 transition-all text-xs font-semibold rounded-xl font-display shadow-lg shadow-green-500/20 cursor-pointer"
                        >
                          WhatsApp
                          <MessageCircle className="w-3.5 h-3.5" />
                        </a>
                      )}
                      {!currentAd.enlace_url && !currentAd.whatsapp && (
                        <span className="text-xs text-text-muted">Apoyando el bracket de SubiGol</span>
                      )}
                    </div>
                  </div>
                </div>

              {/* Layout para escritorio (Flex Col) */}
              <div className="hidden md:flex flex-col w-full items-center">
                {/* Etiqueta arriba del banner */}
                <span className="mb-4 text-[10px] font-bold tracking-widest text-cyan uppercase bg-cyan/15 px-3 py-1 rounded-full border border-cyan/35 shadow-[0_0_10px_rgba(0,194,255,0.05)]">
                  Auspiciador Oficial Mundial 2026
                </span>

                {/* El Banner (La Tarjeta con borde cyan que contiene la imagen en pantalla completa sin recortar, aspect ratio 4:1) */}
                <div className="w-full aspect-[4/1] rounded-2xl overflow-hidden bg-gradient-to-br from-navy-dark/95 via-navy/85 to-navy-dark/95 border-2 border-cyan/40 shadow-[0_0_35px_rgba(0,194,255,0.22)] hover:border-cyan/60 transition-all duration-300 relative">
                  {isVideo ? (
                    <video
                      key={currentAd.id}
                      src={`${baseImgUrl}${currentAd.imagen_ruta}`}
                      className="w-full h-full object-contain block"
                      autoPlay
                      muted
                      loop={parseInt(currentAd?.duracion_video) > 0}
                      playsInline
                      onEnded={handleVideoEnded}
                    />
                  ) : (
                    <img
                      src={`${baseImgUrl}${currentAd.imagen_ruta}`}
                      alt={currentAd.titulo}
                      className="w-full h-full object-contain block"
                      onError={(e) => {
                        e.target.style.display = 'none'
                      }}
                    />
                  )}
                </div>

                {/* Contenido FUERA del banner (debajo) */}
                  <div className="mt-4 w-full flex items-center justify-between px-2 pb-2">
                    <h3 className="font-display font-bold text-xl lg:text-2xl text-text-primary">
                      {currentAd.titulo}
                    </h3>
                    
                    <div className="flex gap-3 items-center">
                      {currentAd.enlace_url && (
                        <a
                          href={currentAd.enlace_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={() => handleAdClick(currentAd)}
                          className="inline-flex items-center gap-2 px-6 py-3 bg-cyan text-navy-dark hover:bg-cyan-light hover:scale-105 active:scale-95 transition-all text-xs font-semibold rounded-xl font-display shadow-lg shadow-cyan/20 hover:shadow-cyan/35 group cursor-pointer"
                        >
                          Visitar Sitio
                          <ExternalLink className="w-3.5 h-3.5" />
                        </a>
                      )}
                      {currentAd.whatsapp && (
                        <a
                          href={getWhatsAppLink(currentAd.whatsapp)}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={() => handleAdClick(currentAd)}
                          className="inline-flex items-center gap-2 px-6 py-3 bg-[#25D366] text-white hover:bg-[#20ba5a] hover:scale-105 active:scale-95 transition-all text-xs font-semibold rounded-xl font-display shadow-lg shadow-green-500/20 group cursor-pointer"
                        >
                          WhatsApp
                          <MessageCircle className="w-3.5 h-3.5" />
                        </a>
                      )}
                      {!currentAd.enlace_url && !currentAd.whatsapp && (
                        <span className="text-xs text-text-muted">Apoyando el bracket de SubiGol</span>
                      )}
                    </div>
                  </div>
                </div>
            </motion.div>
          </AnimatePresence>

          {/* Controles de navegación */}
          {activeAds.length > 1 && (
            <div className="absolute bottom-0 left-1/2 -translate-x-1/2 flex gap-2 z-20">
              {activeAds.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentIndex(index)}
                  className={`w-2.5 h-2.5 rounded-full transition-all ${
                    index === currentIndex 
                      ? 'bg-cyan w-6 shadow-[0_0_8px_rgba(0,194,255,0.8)]' 
                      : 'bg-text-muted/30 hover:bg-cyan/40'
                  }`}
                  aria-label={`Ir a banner ${index + 1}`}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>

  )
}
